"use client";

import { Button } from "@ui/components/button";
import { ArrowUp, Copy } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import type { BatchPromptResult as BatchPromptResultType } from "../hooks/use-batch-image-to-prompt";

export type BatchPromptResultProps = {
	results: BatchPromptResultType[];
	imageFiles: File[];
	onUseInImageGenerator?: (prompt: string) => void;
};

export function BatchPromptResult({
	results,
	imageFiles,
	onUseInImageGenerator,
}: BatchPromptResultProps) {
	const t = useTranslations();
	const [expandedCard, setExpandedCard] = useState<number | null>(null);

	// Toggle expanded state
	const toggleExpand = (index: number) => {
		setExpandedCard(expandedCard === index ? null : index);
	};

	// Handle copy to clipboard
	const handleCopyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
		toast.success(t("batchImageToPrompt.components.results.promptCopied"));
	};

	if (results.length === 0) {
		return null;
	}

	return (
		<div className="@container">
			<h2 className="text-lg font-semibold mb-4 bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
				{t("batchImageToPrompt.components.results.title")}
			</h2>

			<div className="grid grid-cols-1 @min-[640px]:grid-cols-2 gap-4 max-h-[800px] overflow-y-auto pr-1">
				{results.map((result, index) => (
					<div
						key={`batch-result-${index}-${imageFiles[index]?.name || index}`}
						className={`border rounded-lg overflow-hidden shadow-sm ${
							expandedCard === index ? "border-indigo-300" : ""
						}`}
					>
						<div className="flex gap-2">
							<div className="w-16 shrink-0 p-2">
								<div className="w-full aspect-square">
									<img
										src={
											imageFiles[index]
												? URL.createObjectURL(
														imageFiles[index],
													)
												: ""
										}
										alt={t(
											"batchImageToPrompt.components.results.thumbnailAlt",
											{ number: index + 1 },
										)}
										className="w-full h-full object-cover rounded"
									/>
								</div>
							</div>
							<div className="min-w-0 flex-1 p-3 flex flex-col">
								<div className="flex justify-between items-start mb-2">
									<h3 className="font-medium text-sm">
										{t(
											"batchImageToPrompt.components.results.imageNumber",
											{ number: index + 1 },
										)}
									</h3>
									<div className="space-x-1">
										<Button
											size="sm"
											variant="ghost"
											className="h-7 w-7 p-0 text-gray-500"
											onClick={() =>
												handleCopyToClipboard(
													result.prompt || "",
												)
											}
										>
											<Copy className="h-3.5 w-3.5" />
										</Button>
										<Button
											size="sm"
											variant="ghost"
											className="h-7 w-7 p-0 text-gray-500"
											onClick={() => toggleExpand(index)}
										>
											<ArrowUp
												className={`h-3.5 w-3.5 transition-transform ${
													expandedCard === index
														? "rotate-180"
														: ""
												}`}
											/>
										</Button>
									</div>
								</div>

								<div
									className={`text-sm text-gray-700 overflow-hidden transition-all duration-200 ${
										expandedCard === index
											? "max-h-96"
											: "max-h-20"
									}`}
								>
									{result.error ? (
										<p className="text-red-500 text-xs">
											{result.error}
										</p>
									) : (
										<p>{result.prompt}</p>
									)}
								</div>

								{result.prompt && onUseInImageGenerator && (
									<div className="mt-auto pt-2 flex flex-wrap gap-2">
										<Button
											size="sm"
											variant="outline"
											className="text-xs min-h-8 h-auto max-w-full whitespace-normal"
											onClick={() =>
												handleCopyToClipboard(
													result.prompt || "",
												)
											}
										>
											{t(
												"batchImageToPrompt.components.results.copyPrompt",
											)}
										</Button>
										<Button
											size="sm"
											className="text-xs min-h-8 h-auto max-w-full whitespace-normal shadow-lg shadow-blue-500/20 bg-blue-500 hover:bg-blue-600 text-white border-blue-500 transition-all duration-300"
											onClick={() =>
												onUseInImageGenerator(
													result.prompt || "",
												)
											}
										>
											{t(
												"batchImageToPrompt.components.results.useInGenerator",
											)}
										</Button>
									</div>
								)}
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
